import React, { useContext, useEffect, useMemo, useState } from 'react'
import ProofOfDocuments from '../BusinessLiteForms/components/ProofOfDocument';
import { doGET } from '../../../../util/HttpUtil';
import { DialogContext } from '../../../../store/context/DialogContext';
import { useTranslation } from '../../../../store/context/TranslationContext';
import { HiMiniDocumentCheck } from "react-icons/hi2";
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import { FORM_TYPE, IPLUS_WIZARD } from '../IPlushelper';
import { FaSpinner } from 'react-icons/fa';
import useDocTypeRules from '../../../../hooks/useDocTypeRules';
import { DOC_TYPE_FORM_TYPES } from '../../../../util/docTypeRuleDefaults';
import { DOC_TYPE_SCOPE, useDocTypes } from '../../../../util/docTypeRuleMeta';
import { formatIndividualName } from '../../../../util/docTypeRuleEvaluator';

const LABEL_BY_TYPE = {
    "ProofOfAddress": "Proof of Address",
    "Business_Proof_Address": "Business Proof of Address",
}

/* Compoenent for No Documents Required */
const NoDocumentsRequired = () => {
    const { t } = useTranslation();
    return (
        <div className='d-flex align-items-center justify-content-between gap-3 flex-column'>
            <div className='rounded-circle p-2 mt-3' style={{ backgroundColor: "#e0f2fe" }}>
                <HiMiniDocumentCheck size={60} color='#4a75ff' />
            </div>
            <h4 className='fw-semibold text-center fs-3'>No documents needed at this stage</h4>
            <p className='fs-5 fw-medium text-center'>Your information appears consistent so far. We'll notify you if verification materials are required later.</p>
        </div>
    )
};

const FormDocuments = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    isAskForInfo,
    setHasError,
    remark,
    recordId,
    handlePrev,
    isForEDD,
    setErrorMessage = () => { },
    formType,
}) => {
    const [requiredDocTypes, setRequiredDocTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [eddDocLoading, setEddDocLoading] = useState(false);
    const { showError } = useContext(DialogContext);
    const { t } = useTranslation();
    const [isKycVerificationRequired, setIsKycVerificationRequired] = useState(false);
    const { getMeta, loading: docTypesLoading, error: docTypesError } = useDocTypes();

    const docTypeFormType = useMemo(() => {
        if (formType === FORM_TYPE.IndividualPrime) {
            return DOC_TYPE_FORM_TYPES.INDIVIDUAL_PRIME_PR;
        }
        return DOC_TYPE_FORM_TYPES.INDIVIDUAL_PLUS_PR;
    }, [formType]);
    const docRuleApplicationData = useMemo(
        () => ({ ...data, formType }),
        [data, formType],
    );

    const {
        docTypes: ruleDocTypes,
        loading: docRuleLoading,
        error: docRuleError,
    } = useDocTypeRules({
        formType: docTypeFormType,
        applicationData: docRuleApplicationData,
    });

    useEffect(() => {
        if (docRuleError) {
            showError(docRuleError?.message || "Failed to load document rules.");
        }
    }, [docRuleError, showError]);

    useEffect(() => {
        if (docTypesError) {
            showError(docTypesError?.message || "Failed to load document types.");
        }
    }, [docTypesError, showError]);

    /* Checking which documents are required for the provided ubo_id */
    const fetchRequiredDocTypesForUbo = async (id) => {
        try {
            if (!id) return;
            setEddDocLoading(true);
            const response = await doGET(`/api/individual-lite/edd-info?id=${id}`);
            if (response.status === 200) {
                const uboIdsForKYC = response?.data?.uboIdsForKYC;
                if (Array.isArray(uboIdsForKYC)) {
                    setIsKycVerificationRequired(uboIdsForKYC.includes(data?.ubo_id));
                } else {
                    setIsKycVerificationRequired(false);
                }
                const docRes = response?.data?.toCaptureDocs ?? {};
                const docTypesRequired = Array.isArray(docRes?.[data?.ubo_id]) ? docRes[data?.ubo_id] : [];
                setRequiredDocTypes(docTypesRequired);
            }
        } catch (error) {
            console.log(error);
            showError(error?.message || "Failed to load required documents.");
        } finally {
            setEddDocLoading(false);
        }
    }

    useEffect(() => {
        fetchRequiredDocTypesForUbo(data?._id);
    }, [data?._id])

    /* Checking if documents are uploaded id required */
    const checkIsDocumentUploaded = async ({ type }) => {
        if (!recordId || !type) return false;
        try {
            const response = await doGET(`/api/application-doc/grid?rows=-1&form_id=${recordId}${data?.ubo_id ? `&ubo_id=${data?.ubo_id}` : ""}&type=${type}`);
            if (response?.status === 200 && response?.data?.rows[0]?.attachment?.urls?.length) {
                return true;
            }
        } catch (error) {
            showError(error);
        }
        return false;
    };

    const combinedDocTypes = useMemo(
        () => Array.from(new Set([...(requiredDocTypes || []), ...(ruleDocTypes || [])])),
        [requiredDocTypes, ruleDocTypes],
    );
    const isDocumentPageReady = !eddDocLoading && !docRuleLoading && !docTypesLoading;

    /* Submit handler */
    const handleSubmit = async () => {
        const errors = [];
        const docsToValidate = combinedDocTypes?.length ? combinedDocTypes : requiredDocTypes;
        const displayName = formatIndividualName(data);

        try {
            setLoading(true);
            for (const docType of docsToValidate || []) {
                const isDocumentUploaded = await checkIsDocumentUploaded({ type: docType });
                if (!isDocumentUploaded) {
                    const meta = getMeta(docType, { scope: DOC_TYPE_SCOPE.INDIVIDUAL, displayName, individual: data });
                    const message = meta?.errorMessage || "Please upload the required documents.";
                    errors.push(message);
                    showError(message);
                }
            }
            if (errors.length > 0) {
                setHasError(true);
                setErrorMessage(errors.join(' '));
                return;
            }

            setHasError(false);
            await handleUpdate({
                // nextStep: { wizard: isKycVerificationRequired ? IPLUS_WIZARD.KYCVerification : IPLUS_WIZARD.ThankYou },
                skipUpdate: true,
                isLastStep: true,
                isfetchDetail: true
            });
        } catch (error) {
            showError(error?.message || "Failed to submit documents.");
            setHasError(true);
        } finally {
            setLoading(false);
        }
    };

    if (!combinedDocTypes?.length && isDocumentPageReady) {
        return <div>
            <NoDocumentsRequired />
            {!hideButtons && <div className='mt-4'>
                <ButtonGroupNew
                    onNextClick={handleSubmit}
                    onBackClick={() => { handlePrev() }}
                    step={3}
                    limit={isForEDD ? 3 : 4}
                    showPageNum={false}
                    pageNum={3}
                    isDisabled={asLabel}
                    isReadyForReviewAction={isDocumentPageReady}
                    reviewPendingReason="Please wait while document requirements finish loading."
                    asLabel={false}
                    loading={loading || eddDocLoading || docRuleLoading || docTypesLoading}
                />
            </div>}
        </div>
    }

    return (
        <div>
            {eddDocLoading || docRuleLoading || docTypesLoading ? (
                <div style={{ width: '100%', height: 'calc(100vh - 340px)' }} className="d-flex justify-content-center align-items-center w-100 flex-1 fv_ondato_rule">
                    <FaSpinner size={14} className="spinner" />
                </div>
            ) : <>
                {
                    combinedDocTypes?.map((type) => {
                        const meta = getMeta(type, { scope: DOC_TYPE_SCOPE.INDIVIDUAL, displayName: formatIndividualName(data), individual: data }) || {};
                        const label = meta.label || type;
                        return <ProofOfDocuments
                            key={type}
                            dataTestId={meta.dataTestId}
                            isAskForInfo={isAskForInfo}
                            remark={remark}
                            recordId={recordId}
                            asLabel={asLabel}
                            validateByAi={meta.validateByAi}
                            label={t(label)}
                            showCategory={meta.showCategory || type}
                            type={meta.type || type}
                            ubo_id={data?.ubo_id}
                            single
                            supportedFiles={meta.supportedFiles || ['image', 'pdf']}
                        />
                    })
                }
            </>}

            {!hideButtons && <div className='mt-4'>
                <ButtonGroupNew
                    onNextClick={handleSubmit}
                    onBackClick={() => { handlePrev() }}
                    step={3}
                    limit={isForEDD ? 3 : 4}
                    showPageNum={false}
                    pageNum={3}
                    isDisabled={asLabel}
                    isReadyForReviewAction={isDocumentPageReady}
                    reviewPendingReason="Please wait while document requirements finish loading."
                    asLabel={false}
                    loading={loading || eddDocLoading || docRuleLoading || docTypesLoading}
                />
            </div>}
        </div>
    )
}

export default FormDocuments;
